import {
  ORDER_READ_QUERY,
  useOrderReadQuery,
} from "@ticketingplatform/api/dist/order";
import { useTicketUpdateMutation } from "@ticketingplatform/api/dist/ticket";
import { useRefundCreateMutation } from "@ticketingplatform/api/dist/refund";
import { useNumberParams } from "hooks/useNumberParams";
import { useCallback, useMemo, useState } from "react";
import { EyeOutlined } from "@ant-design/icons";
import { Space, Popconfirm } from "antd";
import { DeleteIcon } from "assets/icons";
import { useIntl } from "react-intl";
import { TicketStatus } from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { TicketWithRelations } from "@ticketingplatform/api/dist/ticket/fragments/__generated__/ticketWithRelations";
import useApolloErrorFields from "hooks/useApolloErrorFields";

import { TicketTableProps } from "./partials/TicketTable/types";
import { BillingDescriptionProps } from "./partials/BillingDescription/types";
import { OrderDescriptionProps } from "./partials/OrderDescription/types";
import {
  DEFAULT_ORDER_READ_VARIABLES,
  DEFAULT_TICKET_UPDATE_VARIABLES,
} from "./consts";
import { RefundTableProps } from "./partials/RefundTable/types";
import { RefundModalProps } from "./partials/RefundModal/types";

export const useOrderDetails = () => {
  const { id } = useNumberParams(true);

  const { data, loading, error } = useOrderReadQuery({
    variables: {
      ...DEFAULT_ORDER_READ_VARIABLES,
      id,
    },
  });

  return {
    order: data?.order,
    loading,
    error,
  };
};

export const useOrderDescription = (): OrderDescriptionProps => {
  const { order } = useOrderDetails();

  return useMemo(() => {
    const {
      orderNumber,
      createdAt,
      totalAmount,
      status,
      paymentType,
      paymentMethod,
      receiptUrl,
      tickets,
      discountCode,
    } = order || {};
    const [firstTicket] = tickets?.nodes || [];

    return {
      orderNumber,
      createdAt,
      totalAmount,
      status,
      paymentType,
      paymentMethod,
      receiptUrl,
      discountCode,
      eventDate: firstTicket?.date,
      eventName: firstTicket?.ticketPool?.event?.name,
      eventTimezone: firstTicket?.ticketPool?.event?.timezone || "",
    };
  }, [order]);
};

export const useBillingDescription = (): BillingDescriptionProps => {
  const { order } = useOrderDetails();

  return useMemo(() => order || {}, [order]);
};

export const useTicketTable = (): TicketTableProps<TicketWithRelations> => {
  const intl = useIntl();
  const { order } = useOrderDetails();
  const [ticketUpdateMutation, { loading }] = useTicketUpdateMutation();

  return useMemo(
    () => ({
      dataSource: order?.tickets?.nodes || [],
      pagination: false,
      additionalColumns: [
        {
          title: intl.formatMessage({ defaultMessage: "Action" }),
          key: "action",
          render: (_, { id, name, status, file }) => (
            <Space size="middle">
              {file?.url && (
                <a href={file.url} target="_blank" rel="noreferrer">
                  <EyeOutlined />
                </a>
              )}
              {status === TicketStatus.NEW && (
                <Popconfirm
                  title={intl.formatMessage(
                    {
                      defaultMessage:
                        "Are you sure you want to delete Ticket {name}?",
                    },
                    { name }
                  )}
                  disabled={loading}
                  okButtonProps={{ loading }}
                  onConfirm={() =>
                    ticketUpdateMutation({
                      variables: {
                        ...DEFAULT_TICKET_UPDATE_VARIABLES,
                        id,
                        input: { status: TicketStatus.DELETED },
                      },
                    })
                  }
                >
                  <DeleteIcon />
                </Popconfirm>
              )}
            </Space>
          ),
        },
      ],
    }),
    [intl, loading, order?.tickets?.nodes, ticketUpdateMutation]
  );
};

export const useRefundTable = (): RefundTableProps => {
  const { order } = useOrderDetails();

  return useMemo(
    () => ({
      dataSource: order?.refunds?.nodes || [],
      pagination: false,
    }),
    [order?.refunds?.nodes]
  );
};

export const useRefundModal = (): RefundModalProps & {
  openModal(): void;
  closeModal(): void;
} => {
  const { order } = useOrderDetails();
  const [open, setIsOpen] = useState(false);
  const [refundCreateMutation, { loading, error }] = useRefundCreateMutation({
    evictQueries: [ORDER_READ_QUERY],
  });
  const fields = useApolloErrorFields(error);
  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);
  const onFinish = useCallback<NonNullable<RefundModalProps["onFinish"]>>(
    async ({ input }) => {
      if (!order) {
        return;
      }

      await refundCreateMutation({
        variables: {
          input: {
            ...input,
            orderId: order.id,
          },
        },
      });

      setIsOpen(false);
    },
    [order, refundCreateMutation]
  );

  return {
    open,
    fields,
    loading,
    openModal,
    closeModal,
    onFinish,
    onCancel: closeModal,
    destroyOnClose: true,
  };
};
